import React from "react";
import { useUser } from "@clerk/clerk-react";
import {
  createHistoryThread,
  getHistoryThreads,
  getHistoryMessages,
  updateHistoryThreadTitle,
  fetchWithAuthRetry,
  postThumbs,
} from "@/lib/backend";
import type { ChatRequest, ChatResponse, ChatMessageRow, ChatThread, AgentBlock, ChatSource } from "@shared/api";
import { SignInButton, SignUpButton } from "@clerk/clerk-react";
import TableBlock from "@/components/blocks/TableBlock";
import { useWorkspace } from "@/components/layout/workspace-context";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { isDemoModeScoped } from "@/lib/demoMode";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import SourcePill from "@/components/chat/SourcePill";
import SourceModal from "@/components/chat/SourceModal";

interface ChatViewProps {
  tabId: string;
}

type ChatRole = "user" | "assistant";
type ChatMessage = { role: ChatRole; content: string; created_at?: string; blocks?: AgentBlock[]; message_id?: string; sources?: ChatSource[] };

export default function ChatView({ tabId }: ChatViewProps) {
  const { user, isLoaded, isSignedIn } = useUser();
  const { state: workspaceState, setTabTitle } = useWorkspace();
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [prompt, setPrompt] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [threadId, setThreadId] = React.useState<string | null>(null);
  const [threads, setThreads] = React.useState<ChatThread[]>([]);
  const [threadsLoading, setThreadsLoading] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(false);
  const [loadingOlder, setLoadingOlder] = React.useState(false);
  const [thinkingLabel, setThinkingLabel] = React.useState<string>("");
  const historyRef = React.useRef<HTMLDivElement | null>(null);
  const [betaEmail, setBetaEmail] = React.useState("");
  const [betaSubmitting, setBetaSubmitting] = React.useState(false);
  const [betaSubmitted, setBetaSubmitted] = React.useState(false);
  const pendingTitleRef = React.useRef<string | null>(null);
  const initialBaseTitleRef = React.useRef<string>("");
  const [threadPickerOpen, setThreadPickerOpen] = React.useState(false);
  const [selectedSource, setSelectedSource] = React.useState<ChatSource | null>(null);
  const [threadSearch, setThreadSearch] = React.useState("");

  const isEmptyChat = !isLoading && messages.length === 0;

  const submitBetaSignup = async () => {
    if (!betaEmail || !betaEmail.includes("@")) return;

    setBetaSubmitting(true);
    try {
      await fetchWithAuthRetry(
        "/api/beta-signup",
        (headers) => ({ method: "POST", headers, body: JSON.stringify({ email: betaEmail }) }),
        "beta-signup",
        userHeaders,
      );
      setBetaSubmitted(true);
    } catch {
      // optional: show error later
    } finally {
      setBetaSubmitting(false);
    }
  };

  const userHeaders = React.useMemo(() => {
    if (!isLoaded || !isSignedIn) return undefined;
    const id = user?.id;
    const email = user?.primaryEmailAddress?.emailAddress || undefined;
    return id ? { id, email } : undefined;
  }, [isLoaded, isSignedIn, user?.id, user?.primaryEmailAddress?.emailAddress]);

  const currentTab = React.useMemo(
    () => workspaceState.tabs.find((t) => t.id === tabId),
    [workspaceState.tabs, tabId],
  );

  const deriveDefaultTitle = React.useCallback(() => {
    // IMPORTANT: This must not depend on the current tab title (which can be user-renamed),
    // otherwise new threads can accidentally inherit the previous title.
    //
    // We derive the next "Untitled chat N" from existing thread titles.
    const re = /^untitled chat(?:\s+(\d+))?$/i;
    let max = 0;
    for (const t of threads) {
      const title = (t.title ?? "").trim();
      const m = re.exec(title);
      if (!m) continue;
      const n = m[1] ? Number.parseInt(m[1], 10) : 1;
      if (Number.isFinite(n) && n > max) max = n;
    }
    return `Untitled chat ${max + 1}`;
  }, [threads]);

  const activeThreadTitle = React.useMemo(() => {
    const active = threads.find((t) => t.id === threadId);
    const title = active?.title?.trim();
    return title && title.length > 0 ? title : null;
  }, [threads, threadId]);

  const displayTitle = React.useMemo(
    () => activeThreadTitle ?? deriveDefaultTitle(),
    [activeThreadTitle, deriveDefaultTitle],
  );

  React.useEffect(() => {
    if (!currentTab || !displayTitle) return;
    if (currentTab.title === displayTitle) return;
    setTabTitle(currentTab.id, displayTitle);
  }, [currentTab, displayTitle, setTabTitle]);

  const mapMessages = React.useCallback((rows: ChatMessageRow[]): ChatMessage[] => {
    return (rows || [])
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({
        role: m.role as ChatRole,
        content: m.content,
        created_at: m.created_at,
        blocks: m.blocks,
        sources: m.sources,
      }));
  }, []);

  const loadThreads = React.useCallback(
    async (opts: { limit?: number } = {}) => {
      if (!userHeaders) return { threads: [] as ChatThread[] };
      try {
        setThreadsLoading(true);
        const th = await getHistoryThreads({ limit: opts.limit ?? 20 }, userHeaders);
        setThreads(th?.threads || []);
        return th;
      } catch {
        setThreads([]);
        return { threads: [] as ChatThread[] };
      } finally {
        setThreadsLoading(false);
      }
    },
    [userHeaders],
  );

  const loadMessages = React.useCallback(
    async (targetThreadId: string, opts: { before?: string; replace?: boolean } = {}) => {
      if (!userHeaders || !targetThreadId) return;
      const limit = 50;
      try {
        if (opts.before) setLoadingOlder(true);
        const hm = await getHistoryMessages(
          { threadId: targetThreadId, limit, ...(opts.before ? { before: opts.before } : {}) },
          userHeaders,
        );
        const rows = hm?.messages || [];
        const mapped = mapMessages(rows);
        setHasMore(rows.length === limit);
        if (opts.before) {
          setMessages((prev) => [...mapped, ...prev]);
        } else {
          setMessages(mapped);
        }
        setThreadId(targetThreadId);
      } catch (e: any) {
        if (e?.status === 404) {
          setThreadId(null);
          setMessages([]);
        }
      } finally {
        if (opts.before) setLoadingOlder(false);
      }
    },
    [mapMessages, userHeaders],
  );

  const loadLatestThreadAndMessages = React.useCallback(async () => {
    const th = await loadThreads({ limit: 20 });
    const latest = th?.threads?.[0];
    if (latest?.id) {
      await loadMessages(latest.id, { replace: true });
    } else {
      setThreadId(null);
      setMessages([]);
    }
  }, [loadThreads, loadMessages]);

  // Load latest thread and last 50 messages (when available)
  React.useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) return; // anonymous chat history disabled in workspace chat
    if (!userHeaders) return;
    loadLatestThreadAndMessages();
  }, [isLoaded, isSignedIn, userHeaders, loadLatestThreadAndMessages]);

  const startNewThread = React.useCallback(async () => {
    try {
      const desiredTitle = pendingTitleRef.current ?? deriveDefaultTitle();
      const res = userHeaders
        ? await createHistoryThread({ title: desiredTitle }, userHeaders)
        : null;
      const newId = res?.thread?.id ?? null;
      setThreadId(newId);
      if (desiredTitle) {
        setTabTitle(tabId, desiredTitle);
      }
      pendingTitleRef.current = null;
      setMessages([]);
      setPrompt("");
      setHasMore(false);
      loadThreads({ limit: 20 });
    } catch {
      // ignore
    }
  }, [deriveDefaultTitle, loadThreads, setTabTitle, tabId, userHeaders]);

  // Listen for new thread events from the WindowFrame header
  React.useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{ tabId?: string }>;
      if (custom.detail?.tabId !== tabId) return;
      // Match the thread-selection UX: immediately clear the view and update the header,
      // then create the new thread and load it.
      const nextTitle = deriveDefaultTitle();
      pendingTitleRef.current = nextTitle;
      setTabTitle(tabId, nextTitle);
      setThreadId(null);
      setMessages([]);
      setHasMore(false);
      setPrompt("");

      startNewThread();
    };
    window.addEventListener("chat:new-thread", handler as EventListener);
    return () => window.removeEventListener("chat:new-thread", handler as EventListener);
  }, [deriveDefaultTitle, setTabTitle, startNewThread, tabId]);

  React.useEffect(() => {
    const handleRefresh = (event: Event) => {
      const custom = event as CustomEvent<{ tabId?: string }>;
      if (custom.detail?.tabId !== tabId) return;
      loadThreads({ limit: 20 }).then(() => {
        if (threadId) loadMessages(threadId, { replace: true });
      });
    };

    const handleRename = async (event: Event) => {
      const custom = event as CustomEvent<{ tabId?: string; title?: string }>;
      if (custom.detail?.tabId !== tabId) return;
      const incoming = custom.detail?.title ?? "";
      const nextTitle = incoming.trim() || deriveDefaultTitle();
      setTabTitle(tabId, nextTitle);
      setThreads((prev) =>
        prev.map((t) => (t.id === threadId ? { ...t, title: nextTitle } : t)),
      );
      if (threadId) {
        try {
          await updateHistoryThreadTitle(threadId, nextTitle, userHeaders);
          await loadThreads({ limit: 20 });
        } catch {
          // ignore rename failures; UI already updated
        }
      }
    };

    window.addEventListener("chat:refresh", handleRefresh as EventListener);
    window.addEventListener("chat:rename-thread", handleRename as EventListener);
    return () => {
      window.removeEventListener("chat:refresh", handleRefresh as EventListener);
      window.removeEventListener(
        "chat:rename-thread",
        handleRename as EventListener,
      );
    };
  }, [
    deriveDefaultTitle,
    loadMessages,
    loadThreads,
    setTabTitle,
    tabId,
    threadId,
    userHeaders,
  ]);

  React.useEffect(() => {
    const handleOpenHistory = (event: Event) => {
      const custom = event as CustomEvent<{ tabId?: string }>;
      if (custom.detail?.tabId !== tabId) return;
      setThreadSearch("");
      setThreadPickerOpen(true);
      loadThreads({ limit: 100 });
    };
    window.addEventListener("chat:open-history", handleOpenHistory as EventListener);
    return () =>
      window.removeEventListener("chat:open-history", handleOpenHistory as EventListener);
  }, [loadThreads, tabId]);

  // Auto-scroll to bottom on new messages
  React.useEffect(() => {
    historyRef.current?.scrollTo({
      top: historyRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, isLoading]);

  const describeTool = (name: string): string => {
    const n = name.toLowerCase();
    if (n.includes("notes")) return "Reviewing notes…";
    if (n.includes("tasks")) return "Reviewing tasks…";
    if (n.includes("projects")) return "Reviewing projects…";
    if (n.includes("members")) return "Reviewing project members…";
    return "Retrieving data…";
  };

  const sendMessage = async () => {
    const text = prompt.trim();
    if (!text || isLoading) return;
    setPrompt("");
    setIsLoading(true);
    setThinkingLabel("Thinking…");
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("chat:prompt-submitted", {
          detail: { prompt: text },
        }),
      );
    }

    // Demo mode: show an assistant bubble and skip backend calls (prevents token spend).
    if (
      isSignedIn &&
      isDemoModeScoped({
        userId: user?.id,
        email: user?.primaryEmailAddress?.emailAddress,
      })
    ) {
      setMessages((prev) => [
        ...prev,
        { role: "user", content: text },
        {
          role: "assistant",
          content: "Chat is disabled for this demo.",
        },
      ]);
      setIsLoading(false);
      setThinkingLabel("");
      return;
    }

    // Optimistic user message
    setMessages((prev) => [...prev, { role: "user", content: text }]);

    try {
      // Aggregate a single, copy/pasteable debug bundle per request.
      const sseBundle: any = {
        v: "calcera_sse_bundle_v1",
        ts: new Date().toISOString(),
        threadId: threadId || null,
        request: { message_preview: String(text || "").slice(0, 400) },
        events: [] as any[],
      };

      const history = messages.slice(-10).map((m) => ({ role: m.role, content: m.content }));
      const body: ChatRequest = {
        message: text,
        history,
        ...(threadId ? { threadId } : {}),
      };

      const resp = await fetchWithAuthRetry(
        "/api/chat?debugStream=1",
        (headers) => {
          headers.set("accept", "text/event-stream");
          return { method: "POST", headers, body: JSON.stringify(body) };
        },
        "chat:debugStream",
        userHeaders,
      );
      const contentType = resp.headers.get("content-type") || "";
      try {
        console.debug("[calcera:chat:sse]", {
          status: resp.status,
          contentType,
          url: resp.url,
        });
      } catch {
        // ignore
      }
      if (!contentType.includes("text/event-stream")) {
        // Fallback to JSON response
        const raw = await resp.text();
        try {
          console.debug("[calcera:chat:sse]", {
            mode: "json-fallback",
            bodyPreview: (raw || "").slice(0, 800),
          });
        } catch {
          // ignore
        }
        let data: ChatResponse;
        try {
          data = JSON.parse(raw || "{}") as ChatResponse;
        } catch {
          data = { error: "Invalid JSON from /api/chat (debugStream fallback)" } as any;
        }
        // If backend included OpenAI debug info, log it loudly so it's easy to spot.
        try {
          // Mirror the SSE debug toggle behavior: enabled by default, opt-out via CALCERA_SSE_DEBUG=0
          const logSseFallback =
            (() => {
              try {
                if (typeof localStorage !== "undefined" && localStorage.getItem("CALCERA_SSE_DEBUG") === "0") return false;
                return true;
              } catch {
                return false;
              }
            })();
          if (!logSseFallback) throw new Error("SSE debug disabled");
          const anyData: any = data as any;
          const oai = anyData?.debug?.openai;
          const requestId = oai?.request_id || oai?.requestId;
          if (requestId || oai?.status || oai?.type || oai?.code) {
            const summary = {
              request_id: requestId ?? null,
              status: oai?.status ?? null,
              type: oai?.type ?? null,
              code: oai?.code ?? null,
              param: oai?.param ?? null,
              processing_ms: oai?.processing_ms ?? null,
            };
            console.debug("[calcera:chat:sse]", { mode: "openai", data: summary });
          }
        } catch {
          // ignore
        }
        const assistantContent = (data as any)?.error
          ? `Error: ${(data as any).error}`
          : data.response || "";
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: assistantContent, blocks: data.blocks, message_id: (data as any)?.message_id, sources: data.sources ?? [] },
        ]);
        if (data.threadId) setThreadId(data.threadId);
        return;
      }

      const reader = resp.body?.getReader();
      if (!reader) throw new Error("No stream reader");
      const decoder = new TextDecoder();
      let buffer = "";
      let finalData: ChatResponse | null = null;
      let streamingStarted = false;
      const logSse =
        (() => {
          try {
            // Enabled by default for centralized debugging.
            // Opt-out by setting localStorage: CALCERA_SSE_DEBUG=0
            if (typeof localStorage !== "undefined" && localStorage.getItem("CALCERA_SSE_DEBUG") === "0") return false;
            return true;
          } catch {
            return false;
          }
        })();
      const logOpenAIDebug = (payload: any) => {
        // Keep behavior consistent with the existing calcera SSE console stream:
        // - Enabled by default
        // - Opt-out via localStorage CALCERA_SSE_DEBUG=0
        if (!logSse) return;
        try {
          const oai = payload?.debug?.openai;
          const requestId = oai?.request_id || oai?.requestId;
          if (!requestId && !oai?.status && !oai?.type && !oai?.code) return;
          const summary = {
            request_id: requestId ?? null,
            status: oai?.status ?? null,
            type: oai?.type ?? null,
            code: oai?.code ?? null,
            param: oai?.param ?? null,
            processing_ms: oai?.processing_ms ?? null,
          };
          console.debug("[calcera:sse]", { event: "openai", data: summary });
        } catch {
          // ignore
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const chunk of parts) {
          const lines = chunk.split("\n");
          let eventName = "message";
          let dataLine = "";
          for (const line of lines) {
            if (line.startsWith("event:")) {
              eventName = line.slice(6).trim();
            } else if (line.startsWith("data:")) {
              dataLine += line.slice(5).trim();
            }
          }
          if (logSse) {
            try {
              const parsed = dataLine ? (() => { try { return JSON.parse(dataLine); } catch { return dataLine; } })() : null;
              console.debug("[calcera:sse]", { event: eventName, data: parsed });
              // Store a compact copy for a single shareable bundle per request.
              try {
                sseBundle.events.push({ event: eventName, data: parsed ?? null });
                if (eventName === "evidence_chunks" && parsed && typeof parsed === "object") {
                  sseBundle.evidence_chunks = parsed;
                }
                if (eventName === "compressed_evidence" && parsed && typeof parsed === "object") {
                  sseBundle.compressed_evidence = parsed;
                }
                if (eventName === "retrieval_meta" && parsed && typeof parsed === "object") {
                  sseBundle.retrieval_meta = parsed;
                }
                if (eventName === "gate_policy" && parsed && typeof parsed === "object") {
                  // Day-1 gate policy telemetry (metadata-only). Keep it first-class in the bundle
                  // so a single DevTools copy captures it.
                  sseBundle.gate_policy_v1 = parsed;
                }
              } catch {
                // ignore
              }
              // Always print flat debug lines (they're designed to be readable without expanding objects).
              try {
                if (eventName === "debug" && parsed && typeof parsed === "object") {
                  const p: any = parsed as any;
                  if (p?.kind === "line" && typeof p?.line === "string") {
                    console.log(`[calcera:debug] ${p.line}`);
                  }
                }
              } catch {
                // ignore
              }
              // Extra dev-only visibility: when the backend includes tool result `items`, surface a compact
              // preview of evidence chunks for quick debugging (avoid console spam).
              try {
                if (eventName === "tool" && parsed && typeof parsed === "object") {
                  const p: any = parsed as any;
                  if (p?.kind === "result" && p?.name === "search_workspace") {
                    const evFull = Array.isArray(p?.items?.evidence_chunks) ? p.items.evidence_chunks : [];
                    const evPreview = Array.isArray(p?.itemsPreview?.evidence_chunks) ? p.itemsPreview.evidence_chunks : [];
                    const source = evFull.length ? "items.evidence_chunks" : evPreview.length ? "itemsPreview.evidence_chunks" : "none";
                    const total = evFull.length || (typeof p?.meta?.evidence?.evidence_chunks_returned === "number" ? p.meta.evidence.evidence_chunks_returned : evPreview.length) || 0;

                    // Print as flat lines so it can't get lost in expandable objects.
                    console.log(`[calcera:evidence_chunks] search_workspace evidence_chunks total=${total} source=${source}`);
                    const preview = (evFull.length ? evFull.slice(0, 3) : evPreview.slice(0, 3)).map((c: any) => ({
                      chunk_id: c?.chunk_id ?? null,
                      entity_type: c?.entity_type ?? null,
                      entity_id: c?.entity_id ?? null,
                      chunk_index: c?.chunk_index ?? null,
                      score: c?.score ?? null,
                      title: c?.title ?? null,
                      chunk_text_preview:
                        typeof c?.chunk_text === "string"
                          ? c.chunk_text.replace(/\s+/g, " ").trim().slice(0, 220)
                          : typeof c?.chunk_text_preview === "string"
                            ? String(c.chunk_text_preview)
                            : null,
                    }));
                    console.log(`[calcera:evidence_chunks] preview=${JSON.stringify(preview)}`);
                  }
                }
              } catch {
                // ignore
              }
            } catch {
              // ignore console/log errors
            }
          }
          if (eventName === "token" && dataLine) {
            try {
              const payload = JSON.parse(dataLine);
              const delta = typeof payload?.delta === "string" ? payload.delta : "";
              if (delta) {
                if (!streamingStarted) {
                  streamingStarted = true;
                  setThinkingLabel("");
                  // Optimistically add the assistant bubble so the user sees text immediately
                  setMessages((prev) => [
                    ...prev,
                    { role: "assistant", content: delta },
                  ]);
                } else {
                  setMessages((prev) => {
                    const next = [...prev];
                    const last = next[next.length - 1];
                    if (last && last.role === "assistant") {
                      next[next.length - 1] = { ...last, content: last.content + delta };
                    }
                    return next;
                  });
                }
              }
            } catch {
              // ignore token parse errors
            }
          } else if (eventName === "final" && dataLine) {
            try {
              finalData = JSON.parse(dataLine);
              logOpenAIDebug(finalData);
              try {
                sseBundle.final = finalData;
                (window as any).__calcera_last_sse_bundle = sseBundle;
                if (logSse) {
                  // Emit a first-class console object so devtools users can expand + "Copy object".
                  console.debug("[calcera:sse]", { event: "bundle", data: sseBundle });
                  console.log(`[calcera:sse_bundle] ready (use window.__calcera_last_sse_bundle)`);
                }
              } catch {
                // ignore
              }
              // Sentinel: always emit a small meta log so we can prove the deployed bundle
              // includes the latest SSE debug instrumentation, even when no debug fields exist.
              try {
                const anyFinal: any = finalData as any;
                console.debug("[calcera:sse]", {
                  event: "final_meta",
                  data: {
                    hasError: Boolean(anyFinal?.error),
                    hasDebugOpenAI: Boolean(anyFinal?.debug?.openai),
                    hasDebugServer: Boolean(anyFinal?.debug?.server),
                  },
                });
              } catch {
                // ignore
              }
              // Also surface server-side debug (when present) into the same console stream.
              try {
                const server = (finalData as any)?.debug?.server;
                const sp = server?.second_pass_workspace_context;
                if (sp && typeof sp === "object") {
                  // Print as a flat line so it can't get lost in expandable objects.
                  const returned = (sp as any)?.evidence_chunks_returned ?? null;
                  const withText = (sp as any)?.evidence_chunks_with_text ?? null;
                  const chars = (sp as any)?.tool_payload_chars ?? null;
                  console.log(
                    `[calcera:second_pass_workspace_context] evidence_chunks_returned=${returned} evidence_chunks_with_text=${withText} tool_payload_chars=${chars}`
                  );
                } else if (server && (server.message || server.name)) {
                  console.debug("[calcera:sse]", { event: "server", data: server });
                }
              } catch {
                // ignore
              }
            } catch {
              // ignore parse error
            }
          } else if (eventName === "tool" && dataLine) {
            try {
              const payload = JSON.parse(dataLine);
              const toolName = payload?.name || payload?.tool || payload?.call?.name;
              if (toolName && typeof toolName === "string") {
                const label = describeTool(toolName);
                setThinkingLabel(label);
              }
            } catch {
              // ignore tool parse errors
            }
          } else if (eventName === "retrieval_meta") {
            setThinkingLabel("Searching workspace\u2026");
          } else if (eventName === "evidence_chunks") {
            setThinkingLabel("Reviewing evidence\u2026");
          } else if (eventName === "gate_policy" && dataLine) {
            try {
              const gp = JSON.parse(dataLine);
              const decision = gp?.decision ?? gp?.gate_decision;
              if (decision === "CLARIFY") setThinkingLabel("Forming clarification\u2026");
              else if (decision === "NOT_FOUND") setThinkingLabel("No matching evidence\u2026");
              else setThinkingLabel("Preparing answer\u2026");
            } catch {
              setThinkingLabel("Preparing answer\u2026");
            }
          } else if (eventName === "compressed_evidence") {
            setThinkingLabel("Compressing evidence\u2026");
          } else if (eventName === "records_pack") {
            setThinkingLabel("Building structured data\u2026");
          } else if (eventName === "synthesis_input") {
            setThinkingLabel("Generating response\u2026");
          }
        }
      }

      const payload = finalData;
      if (streamingStarted) {
        // Tokens were already streamed into a live bubble; now merge in metadata from `final`.
        // If final has an error, replace the partial text with the error message.
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === "assistant") {
            if (payload?.error) {
              next[next.length - 1] = {
                ...last,
                content: `Error: ${payload.error}`,
                blocks: undefined,
                message_id: undefined,
                sources: [],
              };
            } else {
              next[next.length - 1] = {
                ...last,
                content: payload?.response || last.content,
                blocks: payload?.blocks,
                message_id: (payload as any)?.message_id,
                sources: payload?.sources ?? [],
              };
            }
          }
          return next;
        });
      } else {
        // No tokens were streamed (e.g. fast path or non-streaming response)
        const assistantContent = payload?.error
          ? `Error: ${payload.error}`
          : payload?.response || "";
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: assistantContent, blocks: payload?.blocks, message_id: (payload as any)?.message_id, sources: payload?.sources ?? [] },
        ]);
      }
      if (payload?.threadId) setThreadId(payload.threadId);
      setThinkingLabel("");
      return;
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Error: Failed to connect to server" },
      ]);
      setThinkingLabel("");
    } finally {
      setIsLoading(false);
      setThinkingLabel("");
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleThumbs = React.useCallback(
    async (vote: "up" | "down", messageContent: string, messageId?: string) => {
      try {
        // Gather context for outcome signal tracking
        const gate_policy_v1 = (() => {
          try {
            return (window as any).__calcera_last_sse_bundle?.gate_policy_v1 ?? undefined;
          } catch {
            return undefined;
          }
        })();
        await postThumbs(
          {
            vote,
            prompt: messageContent,
            thread_id: threadId ?? undefined,
            message_id: messageId,
            gate_policy_v1,
          },
          userHeaders,
        );
        toast({
          title: vote === "up" ? "Thumbs up saved" : "Thumbs down saved",
          description: "Thanks for your feedback!",
        });
      } catch {
        toast({
          title: "Feedback failed",
          description: "Could not save your feedback. Please try again.",
          variant: "destructive",
        });
      }
    },
    [threadId, userHeaders],
  );

  const filteredThreads = React.useMemo(() => {
    const q = threadSearch.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => {
      const title = (t.title || "Untitled").toLowerCase();
      return title.includes(q);
    });
  }, [threadSearch, threads]);

  return (
    <div className="flex h-full w-full flex-col bg-background">
      <Dialog open={threadPickerOpen} onOpenChange={setThreadPickerOpen}>
        <DialogContent className="max-w-[420px] p-4">
          <DialogHeader className="space-y-0">
            <DialogTitle className="text-sm">Chat history</DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            <Input
              value={threadSearch}
              onChange={(e) => setThreadSearch(e.target.value)}
              placeholder="Search threads…"
              className="h-8 text-xs"
            />

            <div className="text-[11px] text-muted-foreground">
              {threadsLoading ? "Loading…" : `${filteredThreads.length} threads`}
            </div>

            <ScrollArea className="h-72 rounded border border-border bg-window">
              <div className="p-1">
                {filteredThreads.length === 0 ? (
                  <div className="px-2 py-2 text-[11px] text-muted-foreground">
                    No threads found.
                  </div>
                ) : (
                  filteredThreads.map((t) => {
                    const isActive = t.id === threadId;
                    const title = t.title?.trim() || "Untitled";
                    const date = new Date(t.last_activity || t.created_at).toLocaleDateString();
                    return (
                      <button
                        key={t.id}
                        type="button"
                        className={`w-full text-left px-2 py-2 rounded hover:bg-background/50 ${
                          isActive ? "bg-background/60" : ""
                        }`}
                        onClick={() => {
                          // Optimistically switch the active thread so the WindowFrame header
                          // (tab title / editable label) updates immediately.
                          setThreadId(t.id);
                          setMessages([]);
                          setHasMore(false);
                          setTabTitle(tabId, t.title?.trim() || deriveDefaultTitle());

                          loadMessages(t.id, { replace: true });
                          setThreadPickerOpen(false);
                        }}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="truncate text-xs">{title}</div>
                          <div className="shrink-0 text-[11px] text-muted-foreground">
                            {date}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Messages */}
      <div
        ref={historyRef}
        className="relative flex-1 overflow-auto p-3 space-y-2"
      >
        {hasMore && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => {
                const oldest = messages[0]?.created_at;
                if (oldest && threadId) {
                  loadMessages(threadId, { before: oldest });
                }
              }}
              disabled={loadingOlder || !threadId}
              className="text-xs px-3 py-1 rounded border border-border bg-window hover:bg-window/80"
            >
              {loadingOlder ? "Loading…" : "Load older"}
            </button>
          </div>
        )}
        {/* Welcome overlay for non-logged-in users with an empty chat */}
          {!isSignedIn && isEmptyChat && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-10 text-center">
            <div className="pointer-events-auto">
              <div className="text-lg font-bold text-purple-200 drop-shadow-[0_0_14px_rgba(168,55,255,0.7)] tracking-wide">
                Welcome to Calcera
              </div>

              <div className="mt-2 max-w-md text-sm leading-relaxed text-purple-300/90">
                Track calculations, decisions, and project progress instantly. <br></br>
                Everything remains saved, structured, and searchable.
              </div>

              <div className="mt-5 text-sm text-purple-400/90">
                Interested in being a beta user?
                <br />
                Sign up now to get early access.
              </div>

              {/* Actions */}
              <div className="mt-6 flex flex-col items-center gap-4">
                {/* Login (Clerk) */}
                <SignInButton mode="modal">
                  <button
                    type="button"
                    className="
                      pointer-events-auto px-4 py-2 text-sm
                      rounded-md bg-primary text-white
                      shadow-md transition

                      hover:bg-primary/90
                      active:scale-95
                    "
                  >
                    Login
                  </button>
                </SignInButton>

                {/* Divider */}
                <div className="text-xs uppercase tracking-wider text-purple-400/70">
                  or
                </div>

                {/* Beta signup */}
                {!betaSubmitted ? (
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={betaEmail}
                      onChange={(e) => setBetaEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="
                        pointer-events-auto h-9 w-56 px-3
                        rounded-md bg-black/40
                        border border-purple-300/40
                        text-purple-100 text-sm
                        placeholder:text-purple-300/40
                        outline-none

                        focus:border-purple-300
                      "
                    />

                    <button
                      type="button"
                      onClick={submitBetaSignup}
                      disabled={betaSubmitting || !betaEmail.includes("@")}
                      className="
                        pointer-events-auto h-9 px-4
                        rounded-md border border-primary
                        text-primary text-sm
                        transition

                        hover:bg-primary/10
                        active:scale-95
                        disabled:opacity-50
                      "
                    >
                      {betaSubmitting ? "Sending…" : "Request access"}
                    </button>
                  </div>
                ) : (
                  <div className="text-sm text-purple-300">
                    Thanks! We’ll be in touch.
                  </div>
                )}
              </div>                    
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={`${i}-${m.role}`}
            className={`group flex ${
              m.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`relative max-w-[80%] rounded-lg border px-3 py-2 text-sm ${
                m.role === "user"
                  ? "bg-primary/15 border-primary/30"
                  : "bg-chat-bubble border-border"
              }`}
            >
              <div className="whitespace-pre-wrap break-words">
                {m.role === "assistant" && m.sources && m.sources.length > 0
                  ? m.content.replace(/\n\nSources:\s*.+$/s, "").trim()
                  : m.content}
              </div>
              {m.role === "assistant" && Array.isArray(m.blocks) && m.blocks.length > 0 ? (
                <div className="mt-2 space-y-2">
                  {m.blocks.map((b: any, idx: number) =>
                    b?.type === "table" && b?.payload?.kind === "table" ? (
                      <TableBlock key={idx} payload={b.payload} />
                    ) : null,
                  )}
                </div>
              ) : null}
              {m.role === "assistant" && m.sources && m.sources.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {m.sources.map((s) => (
                    <SourcePill key={s.entity_id} source={s} onClick={setSelectedSource} />
                  ))}
                </div>
              )}
              {/* Thumbs buttons for assistant messages */}
              {m.role === "assistant" && (
                <div className="absolute -bottom-2 -right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => handleThumbs("up", m.content, m.message_id)}
                    className="w-6 h-6 rounded-full bg-window border border-border flex items-center justify-center hover:bg-window/80"
                    title="Thumbs up"
                    aria-label="Thumbs up"
                  >
                    <ThumbsUp className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleThumbs("down", m.content, m.message_id)}
                    className="w-6 h-6 rounded-full bg-window border border-border flex items-center justify-center hover:bg-window/80"
                    title="Thumbs down"
                    aria-label="Thumbs down"
                  >
                    <ThumbsDown className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {isLoading ? (
          <div className="flex justify-start">
            <div className="rounded-lg border bg-chat-bubble px-3 py-2 text-sm border-border">
              {(() => {
                const raw = thinkingLabel || "Thinking…";
                const base = raw.replace(/…$/, "");
                return (
                  <>
                    <span>{base}</span>
                    <span className="thinking-dots">
                      <span className="thinking-dot">.</span>
                      <span className="thinking-dot">.</span>
                      <span className="thinking-dot">.</span>
                    </span>
                  </>
                );
              })()}
            </div>
          </div>
        ) : null}
      </div>

      {/* Composer */}
      <div className="border-t border-border p-2">
        <div className="flex items-end gap-2 rounded-md border border-border bg-window px-2 py-1">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKey}
            rows={1}
            placeholder="Type a message…"
            className="min-h-[32px] max-h-40 flex-1 resize-none bg-transparent text-sm outline-none"
            style={{ height: "32px" }}
            onInput={(e) => {
              const el = e.currentTarget;
              el.style.height = "32px";
              el.style.height = `${Math.min(160, el.scrollHeight)}px`;
            }}
          />
          <button
            type="button"
            onClick={sendMessage}
            disabled={!prompt.trim() || isLoading}
            className="rounded border bg-primary px-3 py-1 text-sm text-white disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </div>
      <SourceModal source={selectedSource} open={!!selectedSource} onOpenChange={(open) => { if (!open) setSelectedSource(null); }} />
    </div>
  );
}
